// src/pages/MsbReport/MsbReport.jsx (or wherever MsbReport component is located)
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExchangeAlt,
  faHandshake,
  faTentArrowLeftRight,
  faStore
} from '@fortawesome/free-solid-svg-icons';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

// Define the MSB Report options
const msbReportOptions = [
  {
    title: 'Transactions Report',
    icon: faExchangeAlt,
    path: '/msb-report/transactions',
    modules: [3610],
    description: 'View detailed transaction history from all shops under MSB supervision'
  },
  {
    title: 'Shop Commissions',
    icon: faStore,
    path: '/msb-report/shop-commissions',
    modules: [3611],
    description: 'View MSB shop commission earnings from supervised shops'
  },
  {
    title: 'Commission Transactions',
    icon: faTentArrowLeftRight,
    path: '/msb-report/commission-transactions',
    modules: [3612],
    description: 'View MSB commission transactions with add/withdraw details'
  }
];

const MsbReport = () => {
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');
  const [userModules, setUserModules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchUserPrivileges = useCallback(async (userId) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/privileges/modules/${userId}`, {
        timeout: 5000
      });
      
      if (response.status === 200 && response.data.modules) {
        setUserModules(response.data.modules);
      } else {
        setError('No modules found for user');
      }
    } catch (error) {
      setError('Failed to load user privileges');
      console.error('Error fetching user privileges:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      setError('Please login to continue');
      setIsLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserLevel(decodedToken.level);
      setUserId(decodedToken.userId);

      // Handle different user levels
      if (decodedToken.level === 1 || decodedToken.level === 2 || decodedToken.level === 3) {
        // Super admin, State Admin, State Admin Staff get access to all modules
        setUserModules(msbReportOptions.flatMap(option => option.modules));
        setIsLoading(false);
      } else if (decodedToken.level === 4) {
        // MSB gets access to all modules
        setUserModules(msbReportOptions.flatMap(option => option.modules));
        setIsLoading(false);
      } else if (decodedToken.level === 5) {
        // MSB Staff - fetch their specific module permissions
        fetchUserPrivileges(decodedToken.userId);
      } else {
        setError('Invalid user level for MSB reports');
        setIsLoading(false);
      }
    } catch (error) {
      setError('Invalid authentication token');
      setIsLoading(false);
    }
  }, [fetchUserPrivileges]);

  const handleCardClick = useCallback((path) => {
    // For Level 4 users (MSB), go directly to their reports using their userId
    if (userLevel === 4) {
      if (path.includes('commission-transactions')) {
        // Handle Commission Transactions first (most specific)
        navigate(`/msbReport/commission-transactions/msb-commission-transactions/${userId}`);
      } else if (path.includes('transactions') && !path.includes('commission-transactions')) {
        // Handle regular Transactions Report
        navigate(`/msbReport/transactions/msb-transaction/${userId}`);
      } else if (path.includes('shop-commissions')) {
        // Handle Shop Commission Report
        navigate(`/msbReport/shop-commissions/msb-shop-commissions/${userId}`);
      } else {
        navigate(path);
      }
    } else if (userLevel <= 3) {
      // For Level 1, 2, 3 users (Super Admin, State Admin, State Admin Staff), show MSB selection pages
      if (path.includes('commission-transactions')) {
        // Handle Commission Transactions first (most specific)
        navigate('/msb-report/commission-transactions/all-msb-commission-transactions');
      } else if (path.includes('transactions') && !path.includes('commission-transactions')) {
        // Handle regular Transactions Report
        navigate('/msb-report/transactions/all-msb-transactions');
      } else if (path.includes('shop-commissions')) {
        // Handle Shop Commission Report
        navigate('/msb-report/shop-commissions/all-msb-shop-commissions');
      } else {
        navigate(path);
      }
    } else if (userLevel === 5) {
      // For Level 5 users (MSB Staff), show access denied or redirect based on permissions
      navigate(path); // Let the individual pages handle access control
    } else {
      // For other user levels, use default navigation
      navigate(path);
    }
  }, [navigate, userLevel, userId]);

  const hasAccess = useCallback((modules) => {
    return modules.some(moduleId => userModules.includes(moduleId));
  }, [userModules]);

  const getUserLevelText = (level) => {
    switch(level) {
      case 1: return 'Super Admin';
      case 2: return 'State Admin';
      case 3: return 'State Admin Staff';
      case 4: return 'MSB';
      case 5: return 'MSB Staff';
      default: return `Level ${level}`;
    }
  };

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography color="error">{error}</Typography>
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = msbReportOptions.some(option => hasAccess(option.modules));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access MSB reports. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      
      {/* Header with user level info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, mt: -7 }}>
        <Typography variant="h4" component="h1" sx={{ textAlign: 'center', flex: 1 }}>
          MSB Reports
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {getUserLevelText(userLevel)}
        </Typography>
      </Box>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {msbReportOptions.map((option) => (
          hasAccess(option.modules) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 280,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'warning.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      style={{ color: 'orange', marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
      
      {/* Debug Info for development */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Debug Info:</strong> User Level: {userLevel} | User ID: {userId} | 
            Available Modules: [{userModules.join(', ')}]
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MsbReport;